import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import axios from 'axios';
import { Clock, CheckCircle, AlertCircle, Camera, CameraOff, Eye, EyeOff, Users as UsersIcon, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import useAntiCheat from '../hooks/useAntiCheat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Constants
const SUBMISSION_DELAY_MS = 1500;
const TIME_WARNING_THRESHOLD = 60;
const TIME_DANGER_THRESHOLD = 30;
const MAX_WARNINGS = 3;

const ActiveQuiz = () => {
  const { poolId } = useParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [quizData, setQuizData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [quizStartTime, setQuizStartTime] = useState(Date.now());

  // Proctoring: camera + face + object detection extracted to a custom hook
  const {
    videoRef,
    cameraGranted,
    cameraError,
    requestCamera,
    faceStatus,
    warnings: faceWarnings,
    deviceDetected,
    isOverLimit,
  } = useAntiCheat({ maxWarnings: MAX_WARNINGS, submitting });

  const fetchQuestions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/quiz-pools/${poolId}/questions`);
      setQuizData(data);
      setQuestionStartTime(Date.now());
      const startTime = Date.now();
      setQuizStartTime(startTime);
      setTimeRemaining(data.time_limit);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to load quiz questions';
      toast.error(errorMsg);
      navigate('/quiz-pools');
    } finally {
      setLoading(false);
    }
  }, [poolId, navigate]);

  useEffect(() => {
    if (cameraGranted) {
      fetchQuestions();
    }
  }, [fetchQuestions, cameraGranted]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const handleNext = () => {
    if (!selectedAnswer) {
      toast.error('Please select an answer');
      return;
    }

    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
    // Store answer as just the letter (A, B, C, D)
    const answerLetter = selectedAnswer.split(')')[0].trim();
    setAnswers([...answers, { answer: answerLetter, time_taken: timeTaken }]);
    
    if (currentQuestion < quizData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer('');
      setQuestionStartTime(Date.now());
    } else {
      submitQuiz([...answers, { answer: answerLetter, time_taken: timeTaken }]);
    }
  };

  const submitQuiz = useCallback(async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quiz-pools/${poolId}/submit`,
        { pool_id: poolId, answers: finalAnswers }
      );
      await refreshUser();
      toast.success(`Quiz completed! Score: ${data.score}/${data.total} (${data.percentage}%)`);
      
      // Navigate to results page with review data
      setTimeout(() => {
        navigate(`/quiz-results/${poolId}`, { state: data });
      }, SUBMISSION_DELAY_MS);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quiz');
      navigate('/quiz-pools');
    } finally {
      setSubmitting(false);
    }
  }, [poolId, refreshUser, navigate]);

  const autoSubmitQuiz = useCallback(() => {
    // Add current selected answer if any
    let finalAnswers = [...answers];
    if (selectedAnswer) {
      const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);
      const answerLetter = selectedAnswer.split(')')[0].trim();
      finalAnswers.push({ answer: answerLetter, time_taken: timeTaken });
    }
    // Fill remaining questions with empty answers
    while (finalAnswers.length < quizData.questions.length) {
      finalAnswers.push({ answer: '', time_taken: 0 });
    }
    submitQuiz(finalAnswers);
  }, [answers, selectedAnswer, questionStartTime, quizData, submitQuiz]);

  // Countdown timer
  useEffect(() => {
    if (!quizData || submitting) return;

    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - quizStartTime) / 1000);
      const remaining = quizData.time_limit - elapsed;
      
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        toast.error('Time\'s up! Auto-submitting quiz...');
        autoSubmitQuiz();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [quizData, submitting, quizStartTime, autoSubmitQuiz]);

  // Auto-submit when anti-cheat warnings exceed the limit
  useEffect(() => {
    if (isOverLimit && quizData && !submitting) {
      autoSubmitQuiz();
    }
  }, [isOverLimit, quizData, submitting, autoSubmitQuiz]);

  // Camera permission gate
  if (!cameraGranted) {
    return (
      <div className="min-h-screen" data-testid="camera-permission-gate">
        <Header />
        <div className="flex items-center justify-center py-20 px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CameraOff className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Camera Required
            </h2>
            <p className="text-[#6B7280] mb-4">
              To prevent cheating, camera access is required during the quiz. We use AI to detect:
            </p>
            <ul className="text-sm text-[#6B7280] mb-4 space-y-1 text-left max-w-xs mx-auto">
              <li className="flex items-center gap-2"><Eye className="w-4 h-4 text-green-500 shrink-0" /> Face presence (must be visible)</li>
              <li className="flex items-center gap-2"><UsersIcon className="w-4 h-4 text-amber-500 shrink-0" /> Multiple people in frame</li>
              <li className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-orange-500 shrink-0" /> Phones, laptops, or books nearby</li>
            </ul>
            <p className="text-xs text-[#9CA3AF] mb-4">Your video is processed locally and never sent to our servers.</p>
            {cameraError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm mb-4" data-testid="camera-error">
                {cameraError}
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={requestCamera}
                className="px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all flex items-center gap-2"
                data-testid="allow-camera-button"
              >
                <Camera className="w-5 h-5" />
                Allow Camera
              </button>
              <button
                onClick={() => navigate('/quiz-pools')}
                className="px-6 py-3 bg-slate-200 text-[#6B7280] rounded-2xl font-bold hover:bg-slate-300 transition-colors"
                data-testid="go-back-button"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading || submitting) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
            <p className="mt-4 text-[#6B7280]">{submitting ? 'Submitting quiz...' : 'Loading quiz...'}</p>
          </div>
        </div>
      </div>
    );
  }

  const question = quizData?.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizData?.questions.length) * 100;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeRemaining > TIME_WARNING_THRESHOLD) return 'text-[#10B981]';
    if (timeRemaining > TIME_DANGER_THRESHOLD) return 'text-[#FBBF24]';
    return 'text-[#FF6B6B]';
  };

  const getTimerBarColor = () => {
    if (timeRemaining > TIME_WARNING_THRESHOLD) return 'bg-[#10B981]';
    if (timeRemaining > TIME_DANGER_THRESHOLD) return 'bg-[#FBBF24]';
    return 'bg-[#FF6B6B]';
  };

  return (
    <div className="min-h-screen" data-testid="active-quiz-page">
      <Header />

      {/* Camera feed overlay with face + device detection */}
      <div className={`fixed bottom-4 right-4 z-50 rounded-xl overflow-hidden shadow-lg border-2 transition-colors ${
        deviceDetected ? 'border-orange-500 animate-pulse' :
        faceStatus === 'ok' ? 'border-green-400' : faceStatus === 'no_face' ? 'border-red-500 animate-pulse' : faceStatus === 'multiple' ? 'border-amber-500 animate-pulse' : 'border-[#0EA5E9]/30'
      }`} data-testid="camera-feed">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-32 h-24 object-cover bg-black"
        />
        {/* Face status badge */}
        <div className={`absolute top-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
          faceStatus === 'ok' ? 'bg-green-500/80 text-white' : faceStatus === 'no_face' ? 'bg-red-500/80 text-white' : faceStatus === 'multiple' ? 'bg-amber-500/80 text-white' : 'bg-blue-500/80 text-white'
        }`} data-testid="face-status-badge">
          {faceStatus === 'ok' && <><Eye className="w-2.5 h-2.5" /> OK</>}
          {faceStatus === 'no_face' && <><EyeOff className="w-2.5 h-2.5" /> No Face</>}
          {faceStatus === 'multiple' && <><UsersIcon className="w-2.5 h-2.5" /> Multi</>}
          {faceStatus === 'detecting' && <><div className="w-2 h-2 bg-white rounded-full animate-pulse" /> Loading</>}
        </div>
        {/* Device detection badge */}
        {deviceDetected && (
          <div className="absolute top-1 right-1 flex items-center gap-1 px-1.5 py-0.5 bg-orange-500/90 rounded text-[10px] text-white font-bold animate-pulse" data-testid="device-detected-badge">
            <Smartphone className="w-2.5 h-2.5" />
            {deviceDetected}
          </div>
        )}
        {/* Warning count */}
        {faceWarnings > 0 && (
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-red-500/80 rounded text-[10px] text-white font-bold" data-testid="face-warning-count">
            {faceWarnings}/{MAX_WARNINGS}
          </div>
        )}
      </div>
      
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Timer Bar */}
        <div className="mb-8 quiz-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className={`w-6 h-6 ${getTimerColor()}`} />
              <span className="text-sm font-bold text-[#6B7280]">Time Remaining</span>
            </div>
            <span className={`text-3xl font-black ${getTimerColor()}`} style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-1000 ${getTimerBarColor()}`}
              style={{ width: `${(timeRemaining / quizData?.time_limit) * 100}%` }}
            ></div>
          </div>
          {timeRemaining <= TIME_DANGER_THRESHOLD && (
            <div className="mt-2 flex items-center gap-2 text-[#FF6B6B] animate-pulse">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-bold">Hurry up! Time is running out!</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-bold text-[#6B7280]">
              Question {currentQuestion + 1} of {quizData?.questions.length}
            </span>
            <span className="text-sm font-bold text-[#0EA5E9]">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="h-3 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#0EA5E9] to-[#0284C7] transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Quiz Info */}
        <div className="quiz-card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {quizData?.subject}
              </h2>
              <p className="text-sm text-[#6B7280]">{quizData?.topic}</p>
            </div>
          </div>
        </div>

        {/* Question */}
        <div className="quiz-card p-8 mb-8" data-testid="question-card">
          <h3 className="text-2xl font-bold text-[#111827] mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {question?.question}
          </h3>

          <div className="space-y-4">
            {question?.options.map((option, index) => {
              const optionLetter = option.split(')')[0];
              const isSelected = selectedAnswer === optionLetter;
              
              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(optionLetter)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    isSelected
                      ? 'border-[#0EA5E9] bg-[#0EA5E9]/10'
                      : 'border-[#E5E7EB] hover:border-[#0EA5E9]/50'
                  }`}
                  data-testid={`option-${optionLetter}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-[#0EA5E9] bg-[#0EA5E9]'
                          : 'border-[#E5E7EB]'
                      }`}
                    >
                      {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <span className="font-medium text-[#111827]">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          className="w-full px-6 py-4 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all text-lg"
          data-testid="next-button"
        >
          {currentQuestion < quizData?.questions.length - 1 ? 'Next Question' : 'Submit Quiz'}
        </button>
      </main>
    </div>
  );
};

export default ActiveQuiz;